import { TaskAnalysisRequestParams, TaskLongShortGeneratorParams } from '../../src/reducers/work';
import { FeedbackSource } from '../enums/feedback';
import { UserStatus } from '../enums/user';
import {
    CreateConversationResponseSchema,
    GetConversationResponseSchema,
    GetUserConversationStateResponseSchema,
    GetUserPermissionsResponseSchema,
    MessageActionResponseSchema,
    MessageSchema } from '../types/chat';
import { RatingSubject, UserRating } from '../types/rating';
import { WorkReviewStatus } from '../types/work';
import { accessToken as authAccessToken, refreshToken as authRefreshToken } from './auth';

export const BASE_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/`;
export const EXTENSION_ASSET_NAME = process.env.REACT_APP_EXTENSION_ASSET_NAME;

let controller: AbortController;
let signal: AbortSignal;

function _resetAbortController() {
    controller = new AbortController();
    signal = controller.signal;
}

// init controller and signal
_resetAbortController();

export function signUp(
    email: string,
    password: string,
    code: string,
    client: string,
    firstName?: string,
    lastName?: string,
    githubUser?: string,
    trelloUser?: string,
    upworkUser?: string,
    availabilityWeeklyHours?: number,
    pricePerHour?: number
): Promise<any> {
    return _callAPI('backend/api/v1/signup', false, 'POST', {
        email,
        password,
        code,
        client,
        firstName,
        lastName,
        githubUser,
        trelloUser,
        upworkUser,
        availabilityWeeklyHours,
        pricePerHour
    });
}

export function signIn(email: string, password: string): Promise<any> {
    return _callAPI('backend/api/v1/signin', false, 'POST', { email, password });
}

export function signOut(): Promise<any> {
    return _callAPI('backend/api/v1/signout', true, 'POST');
}

export function refreshToken(): Promise<any> {
    return _callAPI('backend/api/v1/auth/refresh', false, 'POST', undefined, true);
}

export function activateUser(activationToken: string): Promise<any> {
    return _callAPI('backend/api/v1/auth/activate', false, 'POST', { activationToken });
}

export function resendUserActivationEmail(unactivatedAccessToken: string): Promise<any> {
    return _callAPI('backend/api/v1/auth/resend', true, 'POST', undefined, false, false, unactivatedAccessToken);
}

export function resetPassword(email: string): Promise<any> {
    return _callAPI('backend/api/v1/auth/reset', false, 'POST', { email });
}

export function resetPasswordChange(code: string, newPassword?: string): Promise<any> {
    return _callAPI('backend/api/v1/auth/reset/change', false, 'POST', { code, newPassword });
}

export function fetchAvailableWork(currentWorkId?: number): Promise<any> {
    return _callAuthenticatedAPI(
        'backend/api/v1/work/available' + (!!currentWorkId ? `?currentWorkId=${currentWorkId}` : '')
    );
}

export function fetchSpecificWork(workId: number): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/work/available?specificWorkId=${workId}`);
}

export function checkActiveWork(): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/work/active-id', 'GET');
}

export function startWork(
    workId: number,
    startTimeEpochMs: number,
    tzName: string
): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/work/start', 'POST', { workId, startTimeEpochMs, tzName });
}

export function skipWork(
    workId: number,
    startTimeEpochMs: number,
    tzName: string
): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/work/skip', 'POST', { workId, startTimeEpochMs, tzName });
}

export function checkpointWork(
    workId: number,
    durationSeconds: number
): Promise<any> {
    return _callAuthenticatedAPI(
        'backend/api/v1/work/checkpoint', 'POST', { workId, durationSeconds }
    );
}

export function finishWork(
    workId: number,
    durationSeconds: number,
    feedback?: string,
    reviewStatus?: WorkReviewStatus,
    reviewFeedback?: string,
    solutionUrls?: string[],
    attachmentList?: number[],
    solveByReviewer?: boolean,
    blockerDescription?: string
): Promise<any> {
    return _callAuthenticatedAPI(
        'backend/api/v1/work/finish',
        'POST',
        {
            workId,
            durationSeconds,
            feedback,
            reviewStatus,
            reviewFeedback,
            solutionUrls,
            attachmentList,
            solveByReviewer,
            blockerDescription
        }
    );
}

export function getAsyncJob(jobId: string): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/job/${jobId}`);
}

export function fetchWorkHistory(): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/work/history');
}

export function fetchDashboardActiveWork(page: number, resultsPerPage: number): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/stats/work/active?page=${page}&resultsPerPage=${resultsPerPage}`
    );
}

export function fetchDashboardPendingWork(page: number, resultsPerPage: number): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/stats/work/pending?page=${page}&resultsPerPage=${resultsPerPage}`
    );
}

export function fetchDashboardCompletedWork(
    page: number,
    resultsPerPage: number,
    filter?: {[key: string]: string}
): Promise<any> {
    let endpointUrl = `backend/api/v1/stats/work/completed?page=${page}&resultsPerPage=${resultsPerPage}`;

    if (filter) {
        for (const [ field, value ] of Object.entries(filter)) {
            endpointUrl = endpointUrl.concat(`&filter=${field}=${value}`);
        }
    }

    return _callAuthenticatedAPI(endpointUrl);
}

export function reserveWork(workId: number, userId: string): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/stats/work/${workId}/reserve/${userId}`, 'POST');
}

export function dismissWorkReservation(workId: number, userId: string): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/stats/work/${workId}/reserve/${userId}`, 'DELETE');
}

export function getFileViewUrl(fileId: number ): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/attachment/get-file-view-url/${fileId}`);
}

export function getFileUploadUrl(fileName: string, fileType: string, workId?: number, isBug?: boolean ): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/attachment/get-file-upload-url', 'POST', { fileName, fileType, workId, isBug });
}

export function scanUserAttachment(fileId: number): Promise<any> {
    return _callAuthenticatedAPI(`backend/api/v1/attachment/scan-file/${fileId}`, 'GET');
}

export function getMessageFileUploadUrl(
    fileName: string,
    fileType: string,
    conversationId: number
): Promise<{ url: string; attachmentId: number }> {
    return _callAuthenticatedAPI('waggle/api/v1/message/attachment/get-upload-url', 'POST', {
        fileName,
        fileType,
        conversationId
    });
}

export function getMessageFileViewUrl(
    attachmentId: number
): Promise<{ url: string; name: string; type: string }> {
    return _callAuthenticatedAPI(`waggle/api/v1/message/attachment/get-view-url/${attachmentId}`);
}

export function scanMessageAttachment(
    attachmentId: number
): Promise<{ isSafe: boolean }> {
    return _callAuthenticatedAPI(
        `waggle/api/v1/message/attachment/scan-file/${attachmentId}`
    );
}

export function fetchDashboardInvalidTasks(page: number, resultsPerPage: number): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/stats/task/invalid?page=${page}&resultsPerPage=${resultsPerPage}`
    );
}

export function fetchDashboardCommunity(page: number, resultsPerPage: number): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/stats/user?page=${page}&resultsPerPage=${resultsPerPage}`
    );
}

export function updateUserStatus(userId: string, status: UserStatus): Promise<any> {
    return _callAuthenticatedAPI(
        'backend/api/v1/user/profile',
        'PUT',
        { userId, status }
    );
}

export function fetchDashboardContributors(
    page: number,
    resultsPerPage: number,
    filter?: {[key: string]: string}
): Promise<any> {
    let endpointUrl = `backend/api/v1/stats/contributors?page=${page}&resultsPerPage=${resultsPerPage}`;

    if (filter) {
        for (const [ field, value ] of Object.entries(filter)) {
            endpointUrl = endpointUrl.concat(`&filter=${field}=${value}`);
        }
    }

    return _callAuthenticatedAPI(endpointUrl);
}

export function fetchDashboardContributorHistory(
    userId: string, 
    page: number,
    resultsPerPage: number
): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/stats/contributor/${userId}?page=${page}&resultsPerPage=${resultsPerPage}`
    );
}

export function sendBugReport(details: string, workId?: number | undefined, attachmentList?: number[] | undefined): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/general/report-bug', 'POST', { workId, details, attachmentList, source: FeedbackSource.FRONTEND });
}

export function sendGlobalFeedback(details: string, attachmentList?: number[]): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/general/global-feedback', 'POST', { details, attachmentList, source: FeedbackSource.FRONTEND });
}

export function fetchUserProfile(): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/user/profile');
}

export function updateUserProfile(
    email?: string,
    firstName?: string,
    lastName?: string,
    githubUser?: string,
    trelloUser?: string,
    upworkUser?: string,
    availabilityWeeklyHours?: number,
    pricePerHour?: number,
    skills?: string[]
): Promise<any> {
    return _callAuthenticatedAPI(
        'backend/api/v1/user/profile',
        'PUT',
        {
            email,
            firstName,
            lastName,
            githubUser,
            trelloUser,
            upworkUser,
            availabilityWeeklyHours,
            pricePerHour,
            skills
        }
    );
}

export function fetchAvailableSkills(): Promise<any> {
    return _callAuthenticatedAPI('backend/api/v1/skill');
}

// Praesepe rating api

export function updateRating(
    code: string, subject: RatingSubject, userRating: UserRating
): Promise<any> {
    const score = userRating.score;
    const text = userRating.feedback;
    return _callAuthenticatedAPI(
        'praesepe/api/v1/rating',
        'POST',
        {
            code,
            subject,
            score,
            text
        },
        undefined,
        undefined
    );
}

// Hive infer api

export function updateWorkTypeClassification(
    input: string, predictedOutput: string, correctedOutput: string
): Promise<any> {
    return _callAuthenticatedAPI(
        'pollinator/task-type/api/v1/correct',
        'POST',
        {
            input,
            predictedOutput,
            correctedOutput
        },
        undefined,
        undefined
    );
}

export function getTaskDescriptionAnalysis(
    params: TaskAnalysisRequestParams
): Promise<any> {
    const endpoint = 'pollinator/task-description-feedback/api/v1/predict';
    const body = {
        input: {
            title: params.title,
            description: params.description,
            type: params.type
        }
    };
    return _callAuthenticatedAPI(endpoint, 'POST', body);
}

export function getTaskDescriptionGenerator(
    params: TaskLongShortGeneratorParams
): Promise<any> {
    const endpoint = 'pollinator/task-description-generator/api/v1/predict';
    const body = {
        input: params
    };
    return _callAuthenticatedAPI(endpoint, 'POST', body);
}

export const fetchConversationMessages = (
    conversationId: number,
    page: number,
    resultsPerPage: number,
    entityId: string | null,
    query?: string
): Promise<GetConversationResponseSchema> => {
    return new Promise((resolve) => {
        getConversation(
            conversationId,
            page,
            resultsPerPage,
            entityId,
            query
        ).then((response) => resolve(response));
    });
};

export function createConversation(
    name: string,
    entityId: string,
    projectId: number,
    projectName: string,
    messages?: MessageSchema[]
): Promise<CreateConversationResponseSchema> {
    return _callAuthenticatedAPI(
        'waggle/api/v1/conversation',
        'POST',
        {
            name,
            entityId,
            projectId,
            projectName,
            messages
        }
    );
}

export function getConversation(
    conversationId: number,
    page: number,
    resultsPerPage: number,
    entityId: string | null,
    query?: string
): Promise<GetConversationResponseSchema> {
    const queryParam = query ? `&query=${query}` : '';
    return _callAuthenticatedAPI(
        `waggle/api/v1/conversation/${conversationId}?page=${page}&resultsPerPage=${resultsPerPage}${queryParam}&entityId=${entityId}`,
        'GET'
    );
}

export function getUserConversationState(
    entityId: string,
    resultsPerPage: number
): Promise<GetUserConversationStateResponseSchema> {
    return _callAuthenticatedAPI(
        `waggle/api/v1/conversation/state/${entityId}?resultsPerPage=${resultsPerPage}`,
        'GET'
    );
}

export function sendMessage(
    conversationId: number,
    entityId: string,
    tagId: string,
    text: string,
    timestampEpochMs: number,
    replyMessageId?: number,
    attachmentIds?: number[]
): Promise<MessageActionResponseSchema> {
    return _callAuthenticatedAPI(
        'waggle/api/v1/message',
        'POST',
        {
            conversationId,
            entityId,
            text,
            timestampEpochMs,
            replyMessageId,
            tagId,
            attachmentIds
        }
    );
}

export function deleteMessage(
    conversationId: number,
    messageId: number
): Promise<MessageActionResponseSchema> {
    return _callAuthenticatedAPI(
        'waggle/api/v1/message',
        'DELETE',
        {
            conversationId,
            messageId
        }
    );
}

export function addReaction(
    conversationId: number,
    messageId: number,
    emoji: string
): Promise<any> {
    return _callAuthenticatedAPI('waggle/api/v1/message/reaction', 'POST', {
        conversationId,
        messageId,
        emoji
    });
}

export function removeReaction(
    conversationId: number,
    messageId: number,
    emoji: string
): Promise<any> {
    return _callAuthenticatedAPI('waggle/api/v1/message/reaction', 'DELETE', {
        conversationId,
        messageId,
        emoji
    });
}

export function getUserPermissions(): Promise<GetUserPermissionsResponseSchema> {
    return _callAuthenticatedAPI(
        'waggle/api/v1/conversation/permission',
        'GET'
    );
}

export function updateQuestBlameIssue(issueId: number): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/quest/investigation-issue/${issueId}`,
        'PUT',
        { issueId }
    );
}

export function getExtensionDownloadUrl(): Promise<any> {
    return _callAuthenticatedAPI(
        `assets/api/v1/asset/${EXTENSION_ASSET_NAME}/download`,
        'GET'
    );
}

// ---------------------------------------------------------------------------
// Planning Q&A — same endpoints the dashboards delegator uses. The
// contributor side only needs read-status + submit-answers.
// ---------------------------------------------------------------------------

export function getPlanningSession(sessionId: string): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/quest/plan/${sessionId}`,
        'GET'
    );
}

export type PlanningAnswerItem = {
    questionId: string;
    selectedOptionIds: string[];
    freeText: string | null;
};

export function submitPlanningAnswers(
    sessionId: string,
    answers: PlanningAnswerItem[]
): Promise<any> {
    return _callAuthenticatedAPI(
        `backend/api/v1/quest/plan/${sessionId}/answer`,
        'POST',
        { answers }
    );
}

type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS' | 'HEAD';

function _callAuthenticatedAPI(
    endpoint: string,
    method: MethodType = 'GET',
    body?: {[key: string]: any},
    refresh: boolean = false,
    abortable: boolean = false
): Promise<any> {
    return _callAPI(endpoint, true, method, body, refresh, abortable, undefined)
        .catch((error) => {
            // if this is not an unauthorized error cascade the failure right away
            if (error.status !== 401) {
                return Promise.reject(error);
            }

            // try refreshing the token and calling the api once again
            return authRefreshToken().then(() =>
                _callAPI(endpoint, true, method, body, refresh, abortable, undefined)
            );
        });
}

function _callAPI(
    endpoint: string,
    authenticated: boolean = false,
    method: MethodType = 'GET',
    body?: {[key: string]: any},
    refresh: boolean = false,
    abortable: boolean = false,
    overrideToken?: string
): Promise<any> {
    // Get the current user
    const accessToken = overrideToken || authAccessToken();
    const refreshToken = localStorage.getItem('refresh_token');

    // The header is assumed to be a JSON
    const config = {
        headers: {}
    };

    // Adding a method if needed (default is get)
    if (method) {
        config['method'] = method;
    }

    // Adding a body to the API request
    if (body) {
        config['headers'] = { 'Content-Type': 'application/json' };
        config['body'] = JSON.stringify(body);
    }

    // Adding authentication token for requests that require authentication
    if (authenticated && accessToken) {
        config['headers']['Authorization'] = `Bearer ${accessToken}`;
    }

    // Adding a refresh token is needs a token refresher...
    if (refresh && refreshToken) {
        config['headers']['Authorization'] = `Bearer ${refreshToken}`;
    }

    if (abortable) {
        config['signal'] = signal;
    }

    // And finally calling the request
    console.log(`calling ${endpoint}`);

    return fetch(BASE_URL + endpoint, config)
        .catch(err => {
            console.log(err);

            // reject with unknown error, but aborting an abortable request should be
            // distinguishable as it's not necessarily an erroneous path
            return Promise.reject({
                status: -1,
                aborted: abortable && err.name === 'AbortError'
            });
        })
        .then(response => response.json()
            .catch(err => {
                console.log(err);
                return Promise.reject({ status: response.status });
            })
            .then(responseBody => {
                if (response.ok && responseBody.status === 'ok') {
                    return Promise.resolve(responseBody.data);
                } else {
                    // error and description may not be available
                    return Promise.reject({
                        status: response.status,
                        error: responseBody.error,
                        description: responseBody.description
                    });
                }
            })
        );
}
